// 30-line i18n replacement for react-i18next + i18next (~14 KB gz saved).
// Uses Solid signals so consumers reactively re-render on language change.

import { createSignal } from "solid-js";
import en from "../locales/en.json";
import ru from "../locales/ru.json";

export type Lang = "en" | "ru";
type Dict = Record<string, unknown>;
const RESOURCES: Record<Lang, Dict> = { en, ru };

function detectInitial(): Lang {
  try {
    const stored = localStorage.getItem("lang");
    if (stored === "ru" || stored === "en") return stored;
  } catch {
    /* ignore */
  }
  const tgLang = (window as any)?.Telegram?.WebApp?.initDataUnsafe?.user?.language_code as
    | string
    | undefined;
  return tgLang?.startsWith("ru") ? "ru" : "en";
}

const [lang, setLangSignal] = createSignal<Lang>(detectInitial());
export { lang };

/** Walk a dotted key path inside the current dictionary. */
function lookup(key: string, dict: Dict): string | undefined {
  let cur: any = dict;
  for (const part of key.split(".")) {
    if (cur && typeof cur === "object" && part in cur) cur = cur[part];
    else return undefined;
  }
  return typeof cur === "string" ? cur : undefined;
}

function interpolate(tpl: string, vars?: Record<string, unknown>): string {
  if (!vars) return tpl;
  return tpl.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => {
    const v = vars[name];
    return v == null ? "" : String(v);
  });
}

/** Reactive translator. Re-runs when `lang()` changes (Solid tracking). */
export function t(key: string, vars?: Record<string, unknown>): string {
  const l = lang();
  const tpl = lookup(key, RESOURCES[l]) ?? lookup(key, RESOURCES.en) ?? key;
  return interpolate(tpl, vars);
}

export function setLanguage(next: Lang): void {
  setLangSignal(next);
  try {
    localStorage.setItem("lang", next);
  } catch {
    /* ignore */
  }
}
